import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  Box,
  Typography,
  Grid,
  TextField,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Stack,
  Divider
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Refresh
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const PipelineEndpoints = ({ pipelineId, pipelineType }) => {
  const theme = useTheme();
  const [deployment, setDeployment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Only show for Norsk pipelines
  if (pipelineType !== 'norsk') {
    return null;
  }

  const fetchDeployment = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/pipelines/${pipelineId}/deployment`);
      if (response.ok) {
        const data = await response.json();
        setDeployment(data);
        setError(null);
      } else if (response.status === 404) {
        setDeployment({ status: 'not_deployed' });
        setError(null);
      } else {
        throw new Error('Failed to fetch deployment status');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const deployPipeline = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/pipelines/${pipelineId}/deploy`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'deploying') {
          setDeployment({ status: 'starting' });
          // Poll for deployment completion
          pollDeployment();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to deploy pipeline');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const undeployPipeline = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/pipelines/${pipelineId}/deploy`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setDeployment({ status: 'stopping' });
        // Poll for undeployment completion
        pollDeployment();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to undeploy pipeline');
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const pollDeployment = () => {
    const interval = setInterval(async () => {
      await fetchDeployment();
      if (deployment &&
          deployment.status !== 'starting' &&
          deployment.status !== 'stopping') {
        clearInterval(interval);
      }
    }, 2000);

    setTimeout(() => clearInterval(interval), 30000); // Stop polling after 30s
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success';
      case 'starting': return 'warning';
      case 'stopping': return 'warning';
      case 'failed': return 'error';
      case 'stopped': return 'default';
      case 'not_deployed': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'running': return 'RUNNING';
      case 'starting': return 'STARTING';
      case 'stopping': return 'STOPPING';
      case 'failed': return 'FAILED';
      case 'stopped': return 'STOPPED';
      case 'not_deployed': return 'NOT DEPLOYED';
      default: return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  useEffect(() => {
    fetchDeployment();
  }, [pipelineId]);

  const isDeployed = deployment?.status === 'running';
  const isTransitioning = deployment?.status === 'starting' || deployment?.status === 'stopping';

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Container Deployment</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={getStatusText(deployment?.status)}
                color={getStatusColor(deployment?.status)}
                size="small"
              />
              <IconButton size="small" onClick={fetchDeployment}>
                <Refresh />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Box>
        }
      />

      <Collapse in={expanded}>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Deployment Controls */}
          <Box mb={2}>
            <Stack direction="row" spacing={2}>
              {!isDeployed && deployment?.status !== 'starting' && (
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={deployPipeline}
                  disabled={loading || isTransitioning}
                >
                  Deploy Pipeline
                </Button>
              )}

              {isDeployed && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Stop />}
                  onClick={undeployPipeline}
                  disabled={loading || isTransitioning}
                >
                  Stop Pipeline
                </Button>
              )}

              {(loading || isTransitioning) && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    {deployment?.status === 'starting' && 'Starting Norsk container...'}
                    {deployment?.status === 'stopping' && 'Stopping container...'}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Deployment Status */}
          {isTransitioning && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {deployment?.status === 'starting' &&
                  'Deploying pipeline container and allocating ports...'}
                {deployment?.status === 'stopping' &&
                  'Stopping container and releasing ports...'}
              </Typography>
            </Alert>
          )}

          {/* Streaming Endpoints */}
          {deployment?.endpoints && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Streaming Endpoints
              </Typography>

              <Grid container spacing={2}>
                {/* SRT Input */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        📡 SRT Input
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {deployment.endpoints.srt?.description || 'SRT streaming input endpoint'}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          value={deployment.endpoints.srt?.url || deployment.endpoints.rtmp?.url || 'Not available'}
                          size="small"
                          fullWidth
                          InputProps={{
                            readOnly: true,
                            style: { fontFamily: 'monospace' }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(deployment.endpoints.srt?.url || deployment.endpoints.rtmp?.url)}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Port: {deployment.endpoints.srt?.port || deployment.endpoints.rtmp?.port || 'Unknown'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* HLS Output */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        🎬 HLS Stream
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {deployment.endpoints.hls.description}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          value={deployment.endpoints.hls.url}
                          size="small"
                          fullWidth
                          InputProps={{
                            readOnly: true,
                            style: { fontFamily: 'monospace' }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(deployment.endpoints.hls.url)}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Port: {deployment.endpoints.hls.port}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* DASH Output */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        📺 DASH Stream
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {deployment.endpoints.dash.description}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          value={deployment.endpoints.dash.url}
                          size="small"
                          fullWidth
                          InputProps={{
                            readOnly: true,
                            style: { fontFamily: 'monospace' }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(deployment.endpoints.dash.url)}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Port: {deployment.endpoints.dash.port}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Quality Metrics */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        📊 Quality Metrics
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {deployment.endpoints.metrics.description}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          value={deployment.endpoints.metrics.url}
                          size="small"
                          fullWidth
                          InputProps={{
                            readOnly: true,
                            style: { fontFamily: 'monospace' }
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(deployment.endpoints.metrics.url)}
                        >
                          <ContentCopy />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Port: {deployment.endpoints.metrics.port}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                💡 Copy the SRT URL to your streaming software (OBS, XSplit, FFmpeg, etc.) to start streaming.
                Use the HLS or DASH URLs in web players to view the stream.
              </Typography>
            </Box>
          )}

          {deployment?.status === 'not_deployed' && (
            <Alert severity="info">
              This pipeline has not been deployed to a container yet.
              Click "Deploy Pipeline" to start a dedicated Norsk container with allocated ports.
            </Alert>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default PipelineEndpoints;